import { create } from 'zustand';

export type EditorMode = 'presentation' | 'edit';
export type ViewMode = 'intro' | 'section' | 'outro';
export type ElementType = 'text' | 'image' | 'youtube' | 'title-block';
export type BackgroundPreset = 'default' | 'blue' | 'purple' | 'green' | 'red' | 'neutral';

// All sections are content sections now (intro/outro are virtual)
export type Section = {
  id: string;
  type: 'content';
  title?: string;
  order: number;
  background: BackgroundPreset;
  imageUrl?: string;
  talkingPoints?: string;
  elements: CanvasElement[];
  connections: Connection[];
};

export type CanvasElement = {
  id: string;
  type: ElementType;
  x: number;
  y: number;
  width: number;
  height: number;
  content?: string;
  src?: string;
  videoId?: string;
  startTime?: number; // YouTube start time in seconds
  fontSize?: number;
  color?: string;
  autoplay?: boolean;
  muted?: boolean;
  border?: boolean;
  shadow?: boolean;
};

export type Connection = {
  id: string;
  from: string;
  to: string;
  style: 'line' | 'arrow' | 'curve';
};

export type Episode = {
  id: string;
  number?: number;
  title: string;
  date?: string;
  showTitle?: string;  // Per-episode show name override (falls back to global settings.showName)
  sections: Section[];
};

type EditorState = {
  mode: EditorMode;
  viewMode: ViewMode;
  currentEpisode: Episode | null;
  currentSectionIndex: number;
  selectedElementId: string | null;
  isConnecting: boolean;
  connectingFrom: string | null;
  cameraSize: number; // 15-35%
  isFullscreen: boolean;
  
  // Actions
  setMode: (mode: EditorMode) => void;
  toggleMode: () => void;
  setViewMode: (viewMode: ViewMode) => void;
  setEpisode: (episode: Episode) => void;
  clearEpisode: () => void;
  setCurrentSection: (index: number) => void;
  nextSection: () => void;
  prevSection: () => void;
  addSection: () => void;
  removeSection: (index: number) => void;
  reorderSections: (newOrder: number[]) => void;
  moveSectionUp: (index: number) => void;
  moveSectionDown: (index: number) => void;
  updateSectionBackground: (background: BackgroundPreset) => void;
  setCameraSize: (size: number) => void;
  
  // Section constraints
  getSections: () => Section[];
  canRemoveSection: (index: number) => boolean;
  canAddSection: () => boolean;
  canMoveSectionUp: (index: number) => boolean;
  canMoveSectionDown: (index: number) => boolean;
  updateSectionImage: (imageUrl: string | undefined) => void;
  updateSectionTitle: (title: string) => void;
  updateSectionById: (sectionId: string, changes: Partial<Pick<Section, 'title' | 'imageUrl' | 'background' | 'talkingPoints'>>) => void;
  
  // Elements
  addElement: (type: ElementType, x: number, y: number) => string | null;
  updateElement: (id: string, changes: Partial<CanvasElement>) => void;
  removeElement: (id: string) => void;
  moveElement: (id: string, x: number, y: number) => void;
  selectElement: (id: string | null) => void;
  duplicateElement: (id: string) => void;
  
  // Connections
  startConnecting: (fromId: string) => void;
  cancelConnecting: () => void;
  createConnection: (toId: string, style: Connection['style'], fromId?: string) => void;
  removeConnection: (id: string) => void;
  
  // Fullscreen
  toggleFullscreen: () => void;
  
  // History for undo
  undo: () => void;
  canUndo: () => boolean;
};

const generateId = () => crypto.randomUUID();

const getStoredCameraSize = (): number => {
  try {
    const stored = localStorage.getItem('cameraSize');
    if (stored) {
      const size = parseFloat(stored);
      if (!isNaN(size) && size >= 15 && size <= 35) return size;
    }
  } catch {}
  return 20;
};

export const useEditorStore = create<EditorState>((set, get) => ({
  mode: 'edit',
  viewMode: 'section',
  currentEpisode: null,
  currentSectionIndex: 0,
  selectedElementId: null,
  isConnecting: false,
  connectingFrom: null,
  cameraSize: getStoredCameraSize(),
  isFullscreen: false,

  setMode: (mode) => set({ mode, selectedElementId: null }),
  
  toggleMode: () => set((state) => ({ 
    mode: state.mode === 'edit' ? 'presentation' : 'edit',
    selectedElementId: null,
    isFullscreen: state.mode === 'edit' ? true : state.isFullscreen,
  })),

  setViewMode: (viewMode) => set({ viewMode, selectedElementId: null }),

  setEpisode: (episode) => set({ 
    currentEpisode: episode, 
    // Don't reset viewMode or currentSectionIndex - let URL routing handle it
  }),

  clearEpisode: () => set({ 
    currentEpisode: null, 
    currentSectionIndex: 0,
    selectedElementId: null,
  }),

  setCurrentSection: (index) => {
    const { currentEpisode } = get();
    if (currentEpisode && index >= 0 && index < currentEpisode.sections.length) {
      set({ 
        currentSectionIndex: index, 
        selectedElementId: null,
      });
    }
  },

  nextSection: () => {
    const { currentEpisode, currentSectionIndex } = get();
    if (currentEpisode && currentSectionIndex < currentEpisode.sections.length - 1) {
      set({ currentSectionIndex: currentSectionIndex + 1 });
    }
  },

  prevSection: () => {
    const { currentSectionIndex } = get();
    if (currentSectionIndex > 0) {
      set({ currentSectionIndex: currentSectionIndex - 1 });
    }
  },

  addSection: () => set((state) => {
    if (!state.currentEpisode) return state;

    const newSection: Section = {
      id: generateId(),
      type: 'content',
      order: state.currentEpisode.sections.length,
      background: 'default',
      elements: [],
      connections: [],
    };

    const sections = [...state.currentEpisode.sections, newSection];

    return {
      currentEpisode: {
        ...state.currentEpisode,
        sections,
      },
    };
  }),

  removeSection: (index) => set((state) => {
    if (!state.currentEpisode) return state;

    const section = state.currentEpisode.sections[index];
    if (!section) return state;

    // Must have at least 1 section
    if (state.currentEpisode.sections.length <= 1) return state;

    const newSections = state.currentEpisode.sections.filter((_, i) => i !== index);
    // Recalculate orders
    const reorderedSections = newSections.map((s, i) => ({ ...s, order: i }));
    const newIndex = Math.min(state.currentSectionIndex, reorderedSections.length - 1);

    return {
      currentEpisode: {
        ...state.currentEpisode,
        sections: reorderedSections,
      },
      currentSectionIndex: newIndex,
    };
  }),

  reorderSections: (newOrder) => set((state) => {
    if (!state.currentEpisode) return state;

    // Validation: must have exact same number of sections
    if (newOrder.length !== state.currentEpisode.sections.length) {
      return state;
    }

    // Validation: must be a valid permutation (no duplicates, all indices present)
    const expectedIndices = new Set(state.currentEpisode.sections.map((_, i) => i));
    const providedIndices = new Set(newOrder);
    if (expectedIndices.size !== providedIndices.size || ![...expectedIndices].every(i => providedIndices.has(i))) {
      return state;
    }

    const reordered = newOrder.map((i) => state.currentEpisode!.sections[i]);
    return {
      currentEpisode: {
        ...state.currentEpisode,
        sections: reordered.map((s, i) => ({ ...s, order: i })),
      },
    };
  }),

  moveSectionUp: (index) => set((state) => {
    if (!state.currentEpisode) return state;
    if (index <= 0) return state;

    const sections = [...state.currentEpisode.sections];
    [sections[index - 1], sections[index]] = [sections[index], sections[index - 1]];

    return {
      currentEpisode: {
        ...state.currentEpisode,
        sections: sections.map((s, i) => ({ ...s, order: i })),
      },
    };
  }),

  moveSectionDown: (index) => set((state) => {
    if (!state.currentEpisode) return state;
    if (index >= state.currentEpisode.sections.length - 1) return state;

    const sections = [...state.currentEpisode.sections];
    [sections[index + 1], sections[index]] = [sections[index], sections[index + 1]];

    return {
      currentEpisode: {
        ...state.currentEpisode,
        sections: sections.map((s, i) => ({ ...s, order: i })),
      },
    };
  }),

  updateSectionBackground: (background) => set((state) => {
    if (!state.currentEpisode) return state;

    const sections = [...state.currentEpisode.sections];
    sections[state.currentSectionIndex] = {
      ...sections[state.currentSectionIndex],
      background,
    };

    return {
      currentEpisode: {
        ...state.currentEpisode,
        sections,
      },
    };
  }),

  updateSectionImage: (imageUrl: string | undefined) => set((state) => {
    if (!state.currentEpisode) return state;

    const sections = [...state.currentEpisode.sections];
    sections[state.currentSectionIndex] = {
      ...sections[state.currentSectionIndex],
      imageUrl,
    };

    return {
      currentEpisode: {
        ...state.currentEpisode,
        sections,
      },
    };
  }),

  updateSectionTitle: (title: string) => set((state) => {
    if (!state.currentEpisode) return state;

    const sections = [...state.currentEpisode.sections];
    sections[state.currentSectionIndex] = {
      ...sections[state.currentSectionIndex],
      title: title || undefined,
    };

    return {
      currentEpisode: {
        ...state.currentEpisode,
        sections,
      },
    };
  }),

  updateSectionById: (sectionId: string, changes: Partial<Pick<Section, 'title' | 'imageUrl' | 'background' | 'talkingPoints'>>) => set((state) => {
    if (!state.currentEpisode) return state;

    const sections = state.currentEpisode.sections.map((section) =>
      section.id === sectionId
        ? { ...section, ...changes }
        : section
    );

    return {
      currentEpisode: {
        ...state.currentEpisode,
        sections,
      },
    };
  }),

  setCameraSize: (size) => {
    const clamped = Math.max(15, Math.min(35, size));
    try { localStorage.setItem('cameraSize', String(clamped)); } catch {}
    set({ cameraSize: clamped });
  },

  addElement: (type, x, y) => {
    const { currentEpisode } = get();
    if (!currentEpisode) return null;
    
    const id = generateId();
    const defaults: Record<ElementType, Partial<CanvasElement>> = {
      text: { content: 'Nouveau texte', width: 200, height: 60, fontSize: 32, color: '#ffffff' },
      image: { src: '', width: 400, height: 300 },
      youtube: { videoId: '', width: 560, height: 315, autoplay: false, muted: true },
      'title-block': { content: 'Titre', width: 150, height: 80, fontSize: 48, color: '#ff6b00' },
    };

    set((state) => {
      if (!state.currentEpisode) return state;
      
      const newElement: CanvasElement = {
        id,
        type,
        x,
        y,
        width: 200,
        height: 50,
        ...defaults[type],
      } as CanvasElement;
      
      // Create new section objects (immutable update)
      const sections = state.currentEpisode.sections.map((section, i) =>
        i === state.currentSectionIndex
          ? { ...section, elements: [...section.elements, newElement] }
          : section
      );
      
      return {
        currentEpisode: {
          ...state.currentEpisode,
          sections,
        },
        selectedElementId: id,
      };
    });
    
    return id;
  },

  updateElement: (id, changes) => set((state) => {
    if (!state.currentEpisode) return state;
    
    // Create new section objects (immutable update)
    const sections = state.currentEpisode.sections.map((section, i) =>
      i === state.currentSectionIndex
        ? {
            ...section,
            elements: section.elements.map((el) =>
              el.id === id ? { ...el, ...changes } : el
            ),
          }
        : section
    );
    
    return {
      currentEpisode: {
        ...state.currentEpisode,
        sections,
      },
    };
  }),

  removeElement: (id) => set((state) => {
    if (!state.currentEpisode) return state;
    
    // Create new section objects (immutable update)
    const sections = state.currentEpisode.sections.map((section, i) =>
      i === state.currentSectionIndex
        ? {
            ...section,
            elements: section.elements.filter((el) => el.id !== id),
            connections: section.connections.filter(
              (c) => c.from !== id && c.to !== id
            ),
          }
        : section
    );
    
    return {
      currentEpisode: {
        ...state.currentEpisode,
        sections,
      },
      selectedElementId: state.selectedElementId === id ? null : state.selectedElementId,
    };
  }),

  moveElement: (id, x, y) => set((state) => {
    if (!state.currentEpisode) return state;
    
    // Create new section objects (immutable update)
    const sections = state.currentEpisode.sections.map((section, i) =>
      i === state.currentSectionIndex
        ? {
            ...section,
            elements: section.elements.map((el) =>
              el.id === id ? { ...el, x, y } : el
            ),
          }
        : section
    );
    
    return {
      currentEpisode: {
        ...state.currentEpisode,
        sections,
      },
    };
  }),

  selectElement: (id) => set({ selectedElementId: id }),

  duplicateElement: (id) => {
    const { currentEpisode, currentSectionIndex } = get();
    if (!currentEpisode) return;
    
    const section = currentEpisode.sections[currentSectionIndex];
    const element = section.elements.find((e) => e.id === id);
    if (!element) return;
    
    const newId = generateId();
    const newElement = {
      ...element,
      id: newId,
      x: element.x + 20,
      y: element.y + 20,
    };
    
    set((state) => {
      // Create new section objects (immutable update)
      const sections = state.currentEpisode!.sections.map((s, i) =>
        i === currentSectionIndex
          ? { ...s, elements: [...s.elements, newElement] }
          : s
      );
      
      return {
        currentEpisode: {
          ...state.currentEpisode!,
          sections,
        },
        selectedElementId: newId,
      };
    });
  },

  startConnecting: (fromId) => set({ isConnecting: true, connectingFrom: fromId }),
  
  cancelConnecting: () => set({ isConnecting: false, connectingFrom: null }),

  createConnection: (toId, style, fromId) => {
    const { currentEpisode, currentSectionIndex } = get();
    const from = fromId ?? get().connectingFrom;
    if (!from || from === toId || !currentEpisode) return;

    const id = generateId();
    const newConnection: Connection = {
      id,
      from: from,
      to: toId,
      style,
    };
    
    set((state) => {
      // Create new section objects (immutable update)
      const sections = state.currentEpisode!.sections.map((s, i) =>
        i === currentSectionIndex
          ? { ...s, connections: [...s.connections, newConnection] }
          : s
      );
      
      return {
        currentEpisode: {
          ...state.currentEpisode!,
          sections,
        },
        isConnecting: false,
        connectingFrom: null,
      };
    });
  },

  removeConnection: (id) => set((state) => {
    if (!state.currentEpisode) return state;
    
    // Create new section objects (immutable update)
    const sections = state.currentEpisode.sections.map((section, i) =>
      i === state.currentSectionIndex
        ? { ...section, connections: section.connections.filter((c) => c.id !== id) }
        : section
    );
    
    return {
      currentEpisode: {
        ...state.currentEpisode,
        sections,
      },
    };
  }),

  toggleFullscreen: () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      set({ isFullscreen: true });
    } else {
      document.exitFullscreen().catch(() => {});
      set({ isFullscreen: false });
    }
  },

  undo: () => {
    console.log('Undo not implemented yet');
  },

  canUndo: () => false,

  getSections: () => {
    const state = get();
    if (!state.currentEpisode) return [];
    return state.currentEpisode.sections;
  },

  canRemoveSection: (index) => {
    const state = get();
    if (!state.currentEpisode) return false;
    if (!state.currentEpisode.sections[index]) return false;
    // Must have at least 1 section
    return state.currentEpisode.sections.length > 1;
  },

  canAddSection: () => {
    const state = get();
    return !!state.currentEpisode;
  },

  canMoveSectionUp: (index: number) => {
    const state = get();
    if (!state.currentEpisode) return false;
    return index > 0;
  },

  canMoveSectionDown: (index: number) => {
    const state = get();
    if (!state.currentEpisode) return false;
    return index < state.currentEpisode.sections.length - 1;
  },
}));
